import React, { useEffect, useState } from 'react';
import { Box, Flex, Text, Icon, Button, SimpleGrid, Center, Spinner, VStack, HStack } from '@chakra-ui/react';
import { FiPhone, FiMapPin, FiDownload, FiFileText, FiCheck, FiClock, FiMaximize2, FiX } from 'react-icons/fi';
import { getSurveyResponse, API_URL, API_URL_BASE } from 'services/surveyService';
import { createLogger } from 'utils/logger';
import FichaFieldValue from 'components/Fichas/fichaFieldRenderer';

const logger = createLogger('FichaPreviewPanel');

const SigBox = ({ label, answer }) => {
  const signed = !!answer;
  return (
    <Box border="1.5px solid" borderColor={signed ? 'ok.500' : 'cream.line'} borderRadius="11px" overflow="hidden">
      <Flex align="center" justify="space-between" px="12px" py="8px"
        bg={signed ? 'ok.bg' : 'cream.bg2'} borderBottom="1px solid" borderColor={signed ? 'ok.500' : 'cream.line'}>
        <Text fontSize="11px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="ink.400">
          {label}
        </Text>
        {signed ? (
          <HStack spacing="4px" color="ok.500" fontSize="11px" fontWeight="700">
            <Icon as={FiCheck} boxSize="12px" /><Text>Firmado</Text>
          </HStack>
        ) : (
          <HStack spacing="4px" color="ink.400" fontSize="11px" fontWeight="700">
            <Icon as={FiClock} boxSize="12px" /><Text>Pendiente</Text>
          </HStack>
        )}
      </Flex>
      <Flex h="83px" align="center" justify="center" bg="white">
        {signed ? (
          <img src={answer.startsWith('http') ? answer : `${API_URL_BASE}${answer}`} alt={label}
            style={{ maxHeight: '65px', maxWidth: '90%', objectFit: 'contain' }} />
        ) : (
          <Text fontSize="12px" color="ink.400" fontStyle="italic">Sin firmar</Text>
        )}
      </Flex>
    </Box>
  );
};

const FichaPreviewPanel = ({ item, customer, onOpenFicha, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item?.id) { setData(null); return; }
    setLoading(true);
    setData(null);
    getSurveyResponse(item.id)
      .then(setData)
      .catch(err => {
        logger.error('Error al cargar ficha:', err);
        setData({ error: 'No se pudo cargar la ficha' });
      })
      .finally(() => setLoading(false));
  }, [item?.id]);

  if (!item) {
    return (
      <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="20px" p="40px 24px" textAlign="center">
        <Icon as={FiFileText} boxSize={8} color="ink.400" mb={3} />
        <Text fontSize="13.5px" color="ink.700" fontWeight="700">Selecciona una ficha</Text>
        <Text fontSize="12.5px" color="ink.400" mt={1}>Haz clic en una ficha de la lista para ver su contenido aquí</Text>
      </Box>
    );
  }

  const pdfUrl = `${API_URL}/generate-pdf/${item.id}/survey-default`;
  const pastel = item.pastel || 'sky';

  const sectionsMap = (data?.questions || []).reduce((acc, question) => {
    const sectionId = question.section.id;
    if (!acc[sectionId]) acc[sectionId] = { name: question.section.name, questions: [] };
    acc[sectionId].questions.push(question);
    return acc;
  }, {});
  const mainSections = Object.values(sectionsMap)
    .map(section => ({ ...section, questions: section.questions.filter(q => !q.text.includes('meta_data') && q.type?.name !== 'signature') }))
    .filter(section => section.questions.length > 0);
  const signatureQuestions = (data?.questions || []).filter(q => q.type?.name === 'signature');
  const signedCount = signatureQuestions.filter(q => q.response?.answer).length;

  return (
    <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="20px" overflow="hidden"
      position="sticky" top="20px">
      <Flex align="center" justify="space-between" px="20px" py="16px" borderBottom="1px solid" borderColor="cream.line">
        <Box minW={0}>
          <Text fontSize="14.5px" fontWeight="800" color="ink.900" noOfLines={1}>{item.survey?.name}</Text>
          <HStack spacing="6px" mt="2px">
            <Box as="span" px="7px" py="1px" borderRadius="999px" bg="mint.bg" color="mint.ink" fontSize="10.5px" fontWeight="800">
              #{item.id}
            </Box>
            <Text fontSize="11.5px" color="ink.400" fontWeight="600">
              Creada {new Date(item.created_at).toLocaleDateString('es-CL')}
            </Text>
          </HStack>
        </Box>
        <HStack spacing="8px" flexShrink={0}>
          {onOpenFicha && (
            <Button size="sm" variant="outline" borderColor="cream.line2" color="ink.700" borderRadius="9px"
              fontWeight="700" fontSize="12.5px" _hover={{ borderColor: 'coral.500', color: 'coral.700' }}
              leftIcon={<Icon as={FiMaximize2} boxSize="12px" />} onClick={() => onOpenFicha(item)}>
              Abrir
            </Button>
          )}
          <Button as="a" href={pdfUrl} target="_blank" rel="noopener noreferrer" size="sm" bg="coral.500" color="white"
            borderRadius="9px" fontWeight="700" fontSize="12.5px" _hover={{ bg: 'coral.600' }}
            leftIcon={<Icon as={FiFileText} boxSize="13px" />}>
            PDF
          </Button>
          {onClose && (
            <Button size="sm" variant="ghost" color="ink.400" borderRadius="9px" minW="auto" p="6px"
              _hover={{ bg: 'cream.bg2', color: 'ink.700' }} onClick={onClose} aria-label="Cerrar previsualización"
              title="Cerrar (Esc)">
              <Icon as={FiX} boxSize="16px" />
            </Button>
          )}
        </HStack>
      </Flex>

      {loading ? (
        <Center py="60px"><Spinner color="coral.500" size="lg" /></Center>
      ) : data?.error ? (
        <Box p="20px" textAlign="center">
          <Text fontSize="13px" color="danger.500" fontWeight="700">{data.error}</Text>
        </Box>
      ) : (
        <>
          <Flex align="center" gap="12px" px="20px" py="14px" borderBottom="1px solid" borderColor="cream.line" bg="cream.bg2">
            <Flex w="40px" h="40px" borderRadius="11px" bg="coral.500" align="center" justify="center" flexShrink={0}>
              <Icon as={FiFileText} color="white" boxSize="18px" />
            </Flex>
            <Box flex={1} minW={0}>
              <Text fontWeight="800" fontSize="13.5px" color="ink.900" noOfLines={1}>Blas Pascal School</Text>
              <Text fontSize="11px" color="ink.400" fontWeight="500" noOfLines={1}>Colegio Osorno</Text>
            </Box>
            <VStack spacing="2px" align="flex-end" fontSize="10.5px" color="ink.400" fontWeight="600" flexShrink={0}>
              <HStack spacing="4px"><Icon as={FiPhone} boxSize="9px" /><Text>+56 9 6762 2809</Text></HStack>
              <HStack spacing="4px"><Icon as={FiMapPin} boxSize="9px" /><Text>Osorno</Text></HStack>
            </VStack>
          </Flex>

          <Flex align="center" justify="space-between" px="20px" py="12px" borderBottom="1px solid" borderColor="cream.line" gap="10px">
            <Box minW={0}>
              <Text fontWeight="800" fontSize="13.5px" color="ink.900" noOfLines={1}>
                {customer?.first_name} {customer?.last_name}
              </Text>
              <Text fontSize="11.5px" color="ink.400" fontWeight="500" noOfLines={1}>{customer?.username}</Text>
            </Box>
            <Box textAlign="right" flexShrink={0}>
              <Text fontSize="10px" color="ink.400" fontWeight="700" textTransform="uppercase">Registro</Text>
              <Text fontSize="13px" fontWeight="800" color={`${pastel}.ink`}>N° {item.id}</Text>
            </Box>
          </Flex>

          <Box p="18px 20px" maxH="480px" overflowY="auto">
            {mainSections.map((section, index) => (
              <Box key={index} mb="20px">
                <Flex align="center" gap="8px" mb="12px">
                  <Text fontSize="11.5px" fontWeight="800" textTransform="uppercase" letterSpacing="0.05em" color="ink.400" whiteSpace="nowrap">
                    {section.name}
                  </Text>
                  <Box flex={1} h="1px" bg="cream.line" />
                </Flex>
                <SimpleGrid columns={2} spacing="12px">
                  {section.questions.map((question, qIndex) => (
                    <Box key={qIndex}>
                      <Text fontSize="10.5px" fontWeight="700" color="ink.400" textTransform="uppercase" letterSpacing="0.05em" mb="3px">
                        {question.text}
                      </Text>
                      <FichaFieldValue question={question} apiUrlBase={API_URL_BASE} />
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            ))}

            {signatureQuestions.length > 0 && (
              <Box>
                <Text fontSize="11.5px" fontWeight="800" textTransform="uppercase" letterSpacing="0.05em" color="ink.400" mb="12px">
                  Firmas
                </Text>
                <SimpleGrid columns={2} spacing="10px">
                  {signatureQuestions.map((question, i) => (
                    <SigBox key={i} label={question.text} answer={question.response?.answer} />
                  ))}
                </SimpleGrid>
              </Box>
            )}
          </Box>

          {signatureQuestions.length > 0 && (
            <Flex align="center" gap="8px" px="20px" py="12px" borderTop="1px solid" borderColor="cream.line" bg="cream.bg2">
              <Icon as={FiCheck} boxSize="13px" color={signedCount === signatureQuestions.length ? 'ok.500' : 'ink.400'} />
              <Text fontSize="12px" color="ink.700" fontWeight="600" flex={1}>
                {signedCount} de {signatureQuestions.length} firmas
              </Text>
              <Button as="a" href={pdfUrl} target="_blank" rel="noopener noreferrer" size="xs" bg="coral.500" color="white"
                borderRadius="8px" fontWeight="700" _hover={{ bg: 'coral.600' }}
                leftIcon={<Icon as={FiDownload} boxSize="11px" />}>
                Descargar PDF
              </Button>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
};

export default FichaPreviewPanel;
